import clsx from 'clsx'
import map from 'lodash/map'
import React, { ReactNode, useMemo } from 'react'

import { useSplitTest } from '@Globals/SplitTests/Hooks'

import styles from './Milestones.module.scss'

interface Milestone {
  buttonClassName?: string
  disabled?: boolean
  icon?: ReactNode
  id: string
  onClick?: () => void
  progress: number
  text?: string | ReactNode
}

const MileStone = ({
  index,
  milestone,
  numberOfMilestones,
}: {
  index: number
  milestone: Milestone
  numberOfMilestones: number
}) => {
  const reached = useMemo(() => milestone.progress === 100, [milestone.progress])
  let firstPercentage = (100 / numberOfMilestones)
  if (numberOfMilestones > 2) {
    firstPercentage /= 1.5
  }

  let otherPercentage = (100 / numberOfMilestones)
  if (numberOfMilestones > 2) {
    otherPercentage += (firstPercentage / (numberOfMilestones - 1))
  }

  return (
    <div
      className={clsx(styles.milestone, 'milestone')}
      style={{ width: index === 0 ? `${firstPercentage}%` : `${otherPercentage}%` }}
    >
      <div className={clsx(styles.milestoneLine, index === 0 && styles.firstLine)}>
        <div
          className={clsx(
            styles.milestoneIndicator,
            index === 0 && styles.firstLine,
            reached && styles.reached,
            milestone.progress > 0 && styles.progressed,
          )}
          style={{ width: `${milestone.progress}%` }}
        />
      </div>
      <button
        className={clsx(
          styles.milestoneBubble,
          milestone.buttonClassName,
          !milestone.disabled && milestone.onClick && styles.clickable,
          reached && styles.reached,
          index === 0 && styles.first,
          index === numberOfMilestones - 1 && styles.last,
        )}
        disabled={milestone.disabled || !milestone.onClick}
        onClick={() => {
          if (!milestone.disabled && milestone.onClick) {
            milestone.onClick()
          }
        }}
        type="button"
      >
        <span>{milestone.icon}</span>
        {milestone.text && (
          <p style={{ paddingLeft: numberOfMilestones > 1 && index === numberOfMilestones - 1 ? `${100 / numberOfMilestones}%` : '0' }}>
            {milestone.text}
          </p>
        )}
      </button>
    </div>
  )
}

export const Milestones = ({
  className,
  hideMilestoneIndicators,
  inverted,
  milestones,
}: {
  className?: string
  hideMilestoneIndicators?: boolean
  inverted?: boolean
  milestones: Milestone[]
}) => {
  const campaignOverrideV1 = useSplitTest('campaign_override_v1') === 'b'

  return (
    <div
      className={clsx(
        className,
        styles.milestones,
        'milestones',
        hideMilestoneIndicators && styles.hideMilestoneIndicators,
        inverted && styles.inverted,
        campaignOverrideV1 && styles.campaignOverride,
      )}
    >
      {map(milestones, (milestone, index) => (
        <MileStone
          key={`milestone_${milestone.id}`}
          index={index}
          milestone={milestone}
          numberOfMilestones={milestones.length}
        />
      ))}
    </div>
  )
}
